import {useState} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {range} from 'lodash'

import TextField from '@mui/material/TextField'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'

import useMediaQuery from '@mui/material/useMediaQuery'
import ScheduleIcon from '@mui/icons-material/Schedule'

import CloseButton from '../../../generics/forms/CloseButton'
import SlideTransition from '../../../generics/SlideTransition'


const styles = {
  root: css`
    cursor: pointer;
  `,
  menuItem: css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 50px;
    height: 40px;
    border-radius: 3px;
  `,
  minuteSelect: css`
    margin: 0 5px;
  `,
  select: css`
    display: flex;
    padding: 0 8px;
  `,
  selectMobile: css`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
  `,
  mobileTimeDisplay: css`
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 30px 0;
  `,
  section: css`
    flex: 1 1 auto;
    margin: 0 8px;
    display: flex;
    flex-direction: column;
  `,
  sectionHeader: css`
    padding-bottom: 10px;
    margin-bottom: 8px;
    font-weight: 600;
    text-align: center;
    border-bottom: 1px solid grey;
  `,
  menuItemMobile: css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 60px;
    height: 35px;
    border-radius: 3px;
  `,
}



const TimePickerField = ({ field, form, disabled, ...props }) => {
  const isXs = useMediaQuery((theme) => theme.breakpoints.only('xs'))
  const [anchorEl, setAnchorEl] = useState(null)

  const { name, value } = field
  const { setFieldValue, handleBlur, isSubmitting } = form

  const hour = value.getHours()
  const minute = value.getMinutes()
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const displayValue = value.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })

  const openMenu = (e) => setAnchorEl(e.currentTarget)
  const closeMenu = () => setAnchorEl(null)

  const update = (newDate) => {
    setFieldValue(name, newDate)
    props.onChange?.(newDate)
  }

  const onHour = (e) => {
    let h = parseInt(e.currentTarget.value, 10)
    if (h === 12 && ampm === 'AM') h = 0
    if (h < 12 && ampm === 'PM') h += 12
    const next = new Date(value); next.setHours(h)
    update(next)
  }
  const onMinute = (e) => {
    const m = parseInt(e.currentTarget.value, 10)
    const next = new Date(value); next.setMinutes(m)
    update(next)
  }
  const onPeriod = (p) => {
    if (p === ampm) return
    const next = new Date(value)
    let h = next.getHours()
    if (p === 'PM' && h < 12) h += 12
    if (p === 'AM' && h >= 12) h -= 12
    next.setHours(h)
    update(next)
  }

  return (
    <div css={styles.root}>
      <div onClick={openMenu}>
        <TextField
          name={name}
          disabled={isSubmitting}
          value={displayValue}
          variant='filled'
          sx={{ input: { cursor: 'pointer' }}}
          InputProps={{
            endAdornment: (
              <IconButton onBlur={handleBlur(name)}>
                <ScheduleIcon />
              </IconButton>
            ),
          }}
          {...props}
        />
      </div>

      {!isXs && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <div css={styles.select}>
            {/* Hours */}
            <div>
              {range(1, 13).map((h) => {
                const disp = hour % 12 === 0 ? 12 : hour % 12
                const sel = disp === h
                return (
                  <MenuItem
                    key={h}
                    value={h}
                    onClick={onHour}
                    css={styles.menuItem}
                    style={{
                      backgroundColor: sel ? '#000' : undefined,
                      color: sel ? '#fff' : undefined,
                    }}
                  >
                    {h}
                  </MenuItem>
                )
              })}
            </div>

            {/* Minutes */}
            <div css={styles.minuteSelect}>
              {range(0, 60, 5).map((m) => {
                const sel = minute === m
                return (
                  <MenuItem
                    key={m}
                    value={m}
                    onClick={onMinute}
                    css={styles.menuItem}
                    style={{
                      backgroundColor: sel ? '#000' : undefined,
                      color: sel ? '#fff' : undefined,
                    }}
                  >
                    {m.toString().padStart(2, '0')}
                  </MenuItem>
                )
              })}
            </div>

            {/* AM/PM */}
            <div>
              {['AM','PM'].map((p) => {
                const sel = p === ampm
                return (
                  <MenuItem
                    key={p}
                    value={p}
                    onClick={() => onPeriod(p)}
                    css={styles.menuItem}
                    style={{
                      backgroundColor: sel ? '#000' : undefined,
                      color: sel ? '#fff' : undefined,
                    }}
                  >
                    {p}
                  </MenuItem>
                )
              })}
            </div>
          </div>
        </Menu>
      )}

      {isXs && (
        <Dialog
          open={Boolean(anchorEl)}
          onClose={closeMenu}
          fullScreen
          fullWidth
          disableScrollLock={false}
          TransitionComponent={SlideTransition}
        >
          <div css={styles.selectMobile}>
            <CloseButton onClick={closeMenu} />

            <div css={styles.mobileTimeDisplay}>
              <h2>{displayValue}</h2>
            </div>

            {/* Hour */}
            <div css={styles.section}>
              <p css={styles.sectionHeader}>HOUR</p>
              {range(1, 13).map((h) => {
                const disp = hour % 12 === 0 ? 12 : hour % 12
                const sel = disp === h
                return (
                  <MenuItem
                    key={h}
                    value={h}
                    onClick={onHour}
                    css={styles.menuItemMobile}
                    style={{
                      backgroundColor: sel ? '#000' : undefined,
                      color: sel ? '#fff' : undefined,
                    }}
                  >
                    {h}
                  </MenuItem>
                )
              })}
            </div>

            {/* Minute */}
            <div css={styles.section}>
              <p css={styles.sectionHeader}>MIN</p>
              {range(0, 60, 5).map((m) => {
                const sel = minute === m
                return (
                  <MenuItem
                    key={m}
                    value={m}
                    onClick={onMinute}
                    css={styles.menuItemMobile}
                    style={{
                      backgroundColor: sel ? '#000' : undefined,
                      color: sel ? '#fff' : undefined,
                    }}
                  >
                    {m.toString().padStart(2, '0')}
                  </MenuItem>
                )
              })}
            </div>

            {/* AM/PM */}
            <div css={styles.section}>
              <p css={styles.sectionHeader}>AM / PM</p>
              {['AM','PM'].map((p) => {
                const sel = p === ampm
                return (
                  <MenuItem
                    key={p}
                    value={p}
                    onClick={() => onPeriod(p)}
                    css={styles.menuItemMobile}
                    style={{
                      backgroundColor: sel ? '#000' : undefined,
                      color: sel ? '#fff' : undefined,
                    }}
                  >
                    {p}
                  </MenuItem>
                )
              })}
            </div>
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default TimePickerField



